import './App.css';
import { useState, useEffect, createContext } from 'react';
import styled from "styled-components";
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import VerifyOtpForm from './pages/VerifyOtpForm';
import LoginForm from './pages/LoginForm';
import Dashboard from './pages/Dashboard';
import Home from './pages/Home';
import RegisterForm from './pages/RegisterForm';
import Profile from './pages/Profile';
import EditProfile from './pages/EditProfile';
import PrivateRoutes from './components/PrivateRoutes';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import PrivateLayout from './components/Layouts/PrivateLayout';
import UserCompanies from './pages/UserCompanies';
import UserCompanyRegistration from './pages/UserCompanyRegistration';
import UserProductRegistration from './pages/UserProductRegistration';
import UserProductRegistrationBulk from './pages/UserProductRegistrationBulk';
import PublicLayout from './components/PublicLayout';
import UserCompanyDetails from './pages/UserCompanyDetails';
import UploadWrapper from './pages/UploadWrapper';
import DraftPage from './pages/DraftPage';
import UserProductDetails from './pages/UserProductDetails';
import CompanyDetails from './pages/CompanyDetails/CompanyDetails';
import ProductDetails from './pages/ProductDetails';
import UserServiceRegisteration from './pages/CompanyServiceRegisteration';
import SpinnerPage from './pages/SpinnerPage';
import UserCompanyDetailsEdit from './pages/UserCompanyDetailsEdit';
import UserProductEdit from './pages/UserProductEdit';
import UserServiceEdit from './pages/UserServiceEdit';
import ContactUsPage from './pages/ContactUsPage';
import AdminCompanyDetails from './pages/Admin/AdminCompanyDetails';
import AdminCompanies from './pages/Admin/AdminCompanies';
import AdminUsers from './pages/Admin/AdminUsers';

import BASE_API_URL from './apis/index';
import axios from 'axios';
import AdminLayout from './components/Layouts/AdminLayout';
import UserLayout from './components/Layouts/UserLayout';
import CompanyCROServiceRegisteration from './pages/CompanyCROServiceRegisteration';
import UserServiceCROEdit from './pages/UserServiceCROEdit';
import AdminUserDetails from './pages/Admin/AdminUserDetails';
import AdminProductDetails from './pages/Admin/AdminProductDetails';

// User Context 
export const UserContext = createContext();

function App() {
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState({
    id: '',
    name: '',
    mobile: '',
    email: '',
    role: '',
    profile_image: ''
  });

  // User Token authentication
  useEffect(() => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      setLoading(false);
      return;
    }

    axios.get(`${BASE_API_URL}/api/auth/verify-token`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => {
        if (res.data.status === 'ok') {
          setUser(res.data);
        }
      })
      .catch((err) => {
        console.error('Token verification failed:', err);
        localStorage.removeItem('authToken');
      })
      .finally(() => setLoading(false));
  }, []);

  // Page Titles
  const titles = {
    '/': 'Home - NT Supply Chain',
    '/register': 'Register - NT Supply Chain',
    '/verify': 'Verify OTP - NT Supply Chain',
    '/login': 'Login - NT Supply Chain',
    '/forgot-password': 'Forgot Password - NT Supply Chain',
    '/reset-password': 'Reset Password - NT Supply Chain',
    '/contact-us': 'Contact Us - NT Supply Chain',
    '/upload-wrapper': 'Upload Wrapper - NT Supply Chain',
    '/draftpage': 'Draft Page - NT Supply Chain',

    '/dashboard': 'Dashboard - NT Supply Chain',
    '/product-details': 'Product Details - NT Supply Chain',
    '/company-details': 'Company Details - NT Supply Chain',

    '/user-companies': 'User Companies - NT Supply Chain',
    '/user-company-register': 'Register Company - NT Supply Chain',
    '/user-company-details': 'User Company Details - NT Supply Chain',

    '/user-products': 'User Products - NT Supply Chain',
    '/user-product-register': 'Register Product - NT Supply Chain',
    '/user-product-details': 'Product Details - NT Supply Chain',
    '/user-product-edit': 'Produc Edit - NT Supply Chain',

    '/user-service-register': 'Register Service - NT Supply Chain',

    '/profile': 'Profile - NT Supply Chain',
    '/edit-profile': 'Edit Profile - NT Supply Chain'
  };

  // Put title to each page
  function TitleManager() {
    const location = useLocation();

    useEffect(() => {
      document.title = titles[location.pathname] || 'NT Supply Chain';
    }, [location]);

    return null;
  }

  if (loading) return <SpinnerPage />;

  return (
    <Wrapper>
      <UserContext.Provider value={{ user, setUser }}>
        <Router>
          <TitleManager />
          <Routes>
            <Route element={<PublicLayout />}>
              <Route path="/" element={<Home />} />
              <Route path="/register" element={<RegisterForm />} />
              <Route path="/verify" element={<VerifyOtpForm />} />
              <Route path="/login" element={<LoginForm />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/contact-us" element={<ContactUsPage />} />

              <Route path="/upload-wrapper" element={<UploadWrapper />} />
              <Route path="/draftpage" element={<DraftPage />} />
            </Route>

            <Route element={<PrivateRoutes />}>
              <Route element={<UserLayout />}>
                <Route path="/company-details" element={<CompanyDetails admin={user.role === "admin"} />} />
                <Route path="/product-details" element={<ProductDetails />} />
              </Route>

              <Route element={<PrivateLayout />}>
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/user-companies" element={<UserCompanies />} />
                <Route path="/user-company-details" element={<UserCompanyDetails />} />
                <Route path="/user-company-details-edit" element={<UserCompanyDetailsEdit />} />
                <Route path="/user-company-register" element={<UserCompanyRegistration />} />

                <Route path="/user-product-register" element={<UserProductRegistration />} />
                <Route path="/user-product-register-bulk" element={<UserProductRegistrationBulk />} />
                <Route path="/user-product-details" element={<UserProductDetails />} />
                <Route path="/user-product-edit" element={<UserProductEdit />} />

                <Route path='/user-service-register' element={<UserServiceRegisteration />} />
                <Route path='/user-service-edit' element={<UserServiceEdit />} />

                <Route path='/user-cro-service-register' element={<CompanyCROServiceRegisteration />} />
                <Route path='/user-cro-service-edit' element={<UserServiceCROEdit />} />

                <Route path="/profile" element={<Profile />} />
                <Route path="/edit-profile" element={<EditProfile />} />
              </Route>

              <Route element={<AdminLayout />}>
                <Route path="/admin/dashboard" element={<AdminCompanies />} />
                <Route path="/admin/companies" element={<AdminCompanies />} />
                <Route path="/admin/company-details" element={<AdminCompanyDetails />} />
                <Route path="/admin/product-details" element={<AdminProductDetails />} />
                <Route path="/admin/users" element={<AdminUsers />} />
                <Route path="/admin/user-details" element={<AdminUserDetails />} />
              </Route>
            </Route>

          </Routes>
        </Router>
      </UserContext.Provider>
    </Wrapper>
  );
}

export default App;

const Wrapper = styled.div`
margin: 0;
padding: 0;
background-image: url("/Images/bg-path10.svg");
background-repeat: no-repeat;
background-position: top center;
background-size: cover;
.after_title_line{
  span{
    position: relative;
    display: inline-block;
    &::after{
      content: '';
      position: absolute;
      left: 110%;
      top: 50%;
      width: 50px;
      height: 2px;
      background: var(--primary-color);
    }
  }
}
`;
